import type { Identifier } from "ra-core";

import {
  toWhatsAppSendErrorCode,
  WhatsAppSendError,
} from "../../whatsapp/whatsappSendError";
import type {
  WhatsAppConversationState,
  WhatsAppCostLabel,
  WhatsAppHealthResult,
  WhatsAppMessage,
  WhatsAppMessageCursor,
  WhatsAppMessagePage,
  WhatsAppSendParams,
  WhatsAppSendResult,
  WhatsAppTemplateCatalogue,
} from "../../types";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic EW2 (TASK-079): Supabase-side WhatsApp conversation methods. Reads go through the
 * RLS-bound tables and the `whatsapp_conversation_deadlines` view of TASK-076, so an asesora only
 * ever gets the conversations of leads she can read. Every clock and cost decision (deadlines,
 * the server `now`, cost labels) is computed by the database; the browser only renders it. Sends
 * go ONLY through the `whatsapp_send` edge function, which alone holds `WHATSAPP_TOKEN`.
 */
export const WHATSAPP_PAGE_SIZE = 50;

const fail = (error: { message: string } | null, fallback: string) => {
  if (error) throw new Error(error.message || fallback);
};

/** Extracts the edge function's `code` from a failed `functions.invoke`. */
const readErrorCode = async (error: unknown): Promise<unknown> => {
  try {
    const context = (error as { context?: { json?: () => Promise<unknown> } })
      ?.context;
    const body = (await context?.json?.()) as { code?: unknown } | undefined;
    return body?.code;
  } catch {
    return undefined;
  }
};

type TemplateRow = {
  id: Identifier;
  name: string;
  language: string;
  label: string;
  variables: unknown;
  is_paid: boolean;
};

export const createSupabaseWhatsAppMethods = () => ({
  /** The newest conversation of a lead with its server clock state, or `null` when it has none. */
  async getWhatsAppConversation(
    dealId: Identifier,
  ): Promise<WhatsAppConversationState | null> {
    const { data, error } = await getSupabaseClient()
      .from("whatsapp_conversation_deadlines")
      .select(
        "conversation_id, deal_id, window_type, deadline_24h, deadline_ctwa, can_free_text, ctwa_open, free_text_cost_label, template_cost_label, server_now",
      )
      .eq("deal_id", dealId)
      .order("last_inbound_at", { ascending: false, nullsFirst: false })
      .limit(1);
    fail(error, "No se pudo leer la conversacion de WhatsApp");
    return ((data ?? [])[0] as WhatsAppConversationState | undefined) ?? null;
  },

  /** One page of a conversation's messages, newest first; `before` is a `{message_at, id}` cursor. */
  async getWhatsAppMessages(
    conversationId: Identifier,
    before?: WhatsAppMessageCursor,
  ): Promise<WhatsAppMessagePage> {
    let query = getSupabaseClient()
      .from("whatsapp_messages")
      .select(
        "id, conversation_id, direction, origin, sender_label, sender_sales_id, type, body, media_id, mime, message_at, kind, template_name, cost_label, status, error",
      )
      .eq("conversation_id", conversationId);
    if (before) {
      query = query.or(
        `message_at.lt.${before.at},and(message_at.eq.${before.at},id.lt.${before.id})`,
      );
    }
    const { data, error } = await query
      .order("message_at", { ascending: false })
      .order("id", { ascending: false })
      .limit(WHATSAPP_PAGE_SIZE + 1);
    fail(error, "No se pudieron leer los mensajes de WhatsApp");
    const rows = (data ?? []) as WhatsAppMessage[];
    return {
      messages: rows.slice(0, WHATSAPP_PAGE_SIZE),
      hasMore: rows.length > WHATSAPP_PAGE_SIZE,
    };
  },

  /**
   * The approved-template catalogue with the cost label of sending each one NOW. The label comes
   * from the server (`template_cost_label` of the conversation); a free template is always
   * 'Gratis'.
   */
  async getWhatsAppTemplates(
    conversationId: Identifier,
  ): Promise<WhatsAppTemplateCatalogue> {
    const client = getSupabaseClient();
    const [config, templates, state] = await Promise.all([
      client
        .from("whatsapp_config")
        .select("templates_enabled")
        .eq("id", 1)
        .maybeSingle(),
      client
        .from("whatsapp_templates")
        .select("id, name, language, label, variables, is_paid")
        .eq("is_active", true)
        .order("label"),
      client
        .from("whatsapp_conversation_deadlines")
        .select("template_cost_label")
        .eq("conversation_id", conversationId)
        .maybeSingle(),
    ]);
    fail(config.error, "No se pudo leer la configuracion de WhatsApp");
    fail(templates.error, "No se pudieron leer las plantillas");
    fail(state.error, "No se pudo leer la conversacion de WhatsApp");
    const serverLabel = (
      state.data as { template_cost_label?: WhatsAppCostLabel } | null
    )?.template_cost_label;
    return {
      templates_enabled: Boolean(
        (config.data as { templates_enabled?: boolean } | null)
          ?.templates_enabled,
      ),
      templates: ((templates.data ?? []) as TemplateRow[]).map((row) => ({
        id: row.id,
        name: row.name,
        language: row.language,
        label: row.label,
        variables: Array.isArray(row.variables)
          ? row.variables.map(String)
          : [],
        cost_label: row.is_paid ? (serverLabel ?? "Con costo") : "Gratis",
      })),
    };
  },

  /**
   * Read-only health check of both numbers through `whatsapp_send` `{mode:'health'}` under the
   * user's session (the function itself only answers direccion). A transport / auth failure
   * throws a generic error; nothing from the failure is echoed.
   */
  async checkWhatsAppHealth(): Promise<WhatsAppHealthResult> {
    const { data, error } = await getSupabaseClient().functions.invoke<
      Partial<WhatsAppHealthResult>
    >("whatsapp_send", { body: { mode: "health" } });
    if (error || !data?.inbox || !data?.alerts) {
      throw new Error("No se pudo comprobar la conexion de WhatsApp");
    }
    return { inbox: data.inbox, alerts: data.alerts };
  },

  /** Sends through the `whatsapp_send` edge function; the token never reaches the browser. */
  async sendWhatsAppMessage(
    params: WhatsAppSendParams,
  ): Promise<WhatsAppSendResult> {
    const body =
      params.type === "text"
        ? {
            conversation_id: params.conversationId,
            type: "text",
            text: params.text,
          }
        : {
            conversation_id: params.conversationId,
            type: "template",
            template_id: params.templateId,
            variables: params.variables,
            confirm_cost: params.confirmCost === true,
          };
    const { data, error } = await getSupabaseClient().functions.invoke<{
      message_id?: Identifier;
      cost_label?: WhatsAppCostLabel;
    }>("whatsapp_send", { body });
    if (error) {
      throw new WhatsAppSendError(
        toWhatsAppSendErrorCode(await readErrorCode(error)),
      );
    }
    return {
      messageId: data?.message_id ?? null,
      costLabel: data?.cost_label ?? "Con costo",
    };
  },
});
